/**
 * Crew card helpers shared by the public card page and the admin crew tools.
 * Pure string building — no Supabase, no DOM — so it is unit-testable and can
 * never drift between the three places that used to copy it.
 */

import { BRAND_LOGO_PNG_BASE64 } from "./brand-logo-base64";

/** URL-safe crew slug: "Micaiah Yoder" -> "micaiah-yoder". */
export function crewSlug(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export type VCardInput = {
  fullName: string;
  title?: string | null;
  phone?: string | null;
  email: string;
  linkedin?: string | null;
  photoBase64?: string | null;
};

/** Fold a long vCard line to 75 octets per line as the spec requires. */
export function foldVCardLine(line: string): string {
  if (line.length <= 75) return line;
  const parts: string[] = [line.slice(0, 75)];
  for (let i = 75; i < line.length; i += 74) parts.push(` ${line.slice(i, i + 74)}`);
  return parts.join("\r\n");
}

/** vCard 3.0 text, CRLF-joined as the spec requires. */
export function buildVCard(opts: VCardInput): string {
  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N:${opts.fullName};${opts.fullName};;;`,
    `FN:${opts.fullName}`,
  ];
  if (opts.title) lines.push(`TITLE:${opts.title}`);
  if (opts.phone) lines.push(`TEL;TYPE=CELL:${opts.phone}`);
  lines.push(`EMAIL:${opts.email}`);
  if (opts.linkedin) lines.push(`URL:${opts.linkedin}`);
  lines.push("ORG:SquadFlight FPV");
  if (opts.photoBase64) {
    lines.push(`PHOTO;ENCODING=b;TYPE=PNG:${opts.photoBase64}`);
  }
  // SquadFlight diamond brand mark travels with every card.
  lines.push(`LOGO;ENCODING=b;TYPE=PNG:${BRAND_LOGO_PNG_BASE64}`);
  if (!opts.photoBase64) {
    lines.push(`PHOTO;ENCODING=b;TYPE=PNG:${BRAND_LOGO_PNG_BASE64}`);
  }
  lines.push("END:VCARD");
  return lines.map(foldVCardLine).join("\r\n");
}
